import React, { Component } from 'react';
import { Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography } from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import moment from 'moment';
import Api from '../../services/api';
import UserSessionService from '../../services/user-session.service';
import { t } from '../usage/usage-i18n';
import DeviceIcon from './device-icon';

// ---------------------------------------------------------------------------
// Item 7 — "Minhas sessões" (qualquer usuário): onde estou logado, desde quando, de que IP/navegador; encerrar as outras
// ("esqueci aberto no computador da recepção"). Só sessões do próprio usuário (a API garante). Aberto pelo menu do usuário.
//   props: intl, open, onClose, onNotify(variant, message)
// ---------------------------------------------------------------------------

const fmt = (v) => (v ? moment(v).format('DD/MM/YYYY HH:mm') : '—');
const shortUa = (ua) => {
  if (!ua) return '—';
  const m = ua.match(/(Edg|Chrome|Firefox|Safari|OPR)\/([\d.]+)/);
  const os = /Windows/.test(ua) ? 'Windows' : /Android/.test(ua) ? 'Android' : /iPhone|iPad/.test(ua) ? 'iOS' : /Mac OS/.test(ua) ? 'macOS' : /Linux/.test(ua) ? 'Linux' : '';
  return m ? `${m[1] === 'Edg' ? 'Edge' : m[1]} ${m[2].split('.')[0]}${os ? ` · ${os}` : ''}` : ua.slice(0, 40);
};

export default class MySessionsDialog extends Component {
  constructor(props) {
    super(props);
    this.state = { loading: false, rows: [], working: false };
    this.load = this.load.bind(this);
    this.revokeOthers = this.revokeOthers.bind(this);
  }

  componentDidUpdate(prevProps) { if (this.props.open && !prevProps.open) this.load(); }

  tr(id, fallback) { return t(this.props.intl, id, fallback); }

  load() {
    this.setState({ loading: true });
    UserSessionService.mine(true)
      .then((rows) => this.setState({ loading: false, rows }))
      .catch((err) => { Api.kickoff(err); this.setState({ loading: false, rows: [] }); if (this.props.onNotify) this.props.onNotify('error', err.message); });
  }

  revokeOne(sessionId) {
    this.setState({ working: true });
    UserSessionService.revokeMine(sessionId)
      .then(() => { if (this.props.onNotify) this.props.onNotify('success', this.tr('session.revokedOne', 'Sessão encerrada.')); this.setState({ working: false }, this.load); })
      .catch((err) => { Api.kickoff(err); this.setState({ working: false }); if (this.props.onNotify) this.props.onNotify('error', err.message); });
  }

  // Encerra todas menos esta, uma a uma (Promise.all; sem async/await em arrow class field).
  revokeOthers() {
    const others = this.state.rows.filter((r) => r.active && !r.current);
    if (!others.length) return;
    this.setState({ working: true });
    Promise.all(others.map((r) => UserSessionService.revokeMine(r.sessionId)))
      .then(() => { if (this.props.onNotify) this.props.onNotify('success', this.tr('session.mine.othersRevoked', 'As outras sessões foram encerradas.')); this.setState({ working: false }, this.load); })
      .catch((err) => { Api.kickoff(err); this.setState({ working: false }, this.load); if (this.props.onNotify) this.props.onNotify('error', err.message); });
  }

  render() {
    const { open, onClose } = this.props;
    const { loading, rows, working } = this.state;
    const others = rows.filter((r) => r.active && !r.current);
    return (
      <Dialog open={!!open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { maxHeight: '90vh', borderRadius: 3 } }}>
        <DialogTitle>
          {this.tr('session.mine.title', 'Minhas sessões')}
          <Typography variant="caption" style={{ display: 'block', color: '#888' }}>{this.tr('session.mine.subtitle', 'Onde você está conectado agora. Encerre o que não reconhecer ou o que ficou aberto em outro computador.')}</Typography>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          <div style={{ maxHeight: '60vh', overflowY: 'auto', padding: '16px 20px' }}>
            {loading && <Box display="flex" justifyContent="center" style={{ padding: 12 }}><CircularProgress size={22} color="secondary" /></Box>}
            {!loading && !rows.length && <Typography variant="body2" style={{ color: '#888' }}>{this.tr('session.mine.none', 'Nenhuma sessão ativa encontrada.')}</Typography>}
            {!loading && rows.length > 0 && (
              <Table size="small">
                <TableHead><TableRow>
                  <TableCell>{this.tr('session.col.started', 'Início')}</TableCell><TableCell>{this.tr('session.col.lastRefresh', 'Última renovação')}</TableCell>
                  <TableCell>IP</TableCell><TableCell>{this.tr('session.col.browser', 'Navegador')}</TableCell><TableCell />
                </TableRow></TableHead>
                <TableBody>
                  {rows.map((r) => (
                    <TableRow key={r.sessionId} hover>
                      <TableCell style={{ whiteSpace: 'nowrap' }}>{fmt(r.createdAt)}{r.current && <Chip size="small" label={this.tr('session.mine.thisOne', 'este navegador')} style={{ marginLeft: 6, height: 20, backgroundColor: '#2e7d32', color: '#fff' }} />}</TableCell>
                      <TableCell style={{ whiteSpace: 'nowrap' }}>{fmt(r.lastRefreshAt)}</TableCell>
                      <TableCell style={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}>{r.ip || '—'}</TableCell>
                      <TableCell style={{ whiteSpace: 'nowrap' }}><DeviceIcon kind={r.deviceKind} intl={this.props.intl} t={t} /><Tooltip title={r.userAgent || ''}><span>{shortUa(r.userAgent)}</span></Tooltip></TableCell>
                      <TableCell align="right">
                        {!r.current && <Button size="small" color="error" startIcon={<LogoutIcon />} disabled={working} onClick={() => this.revokeOne(r.sessionId)}>{this.tr('session.revokeOne.button', 'Encerrar sessão')}</Button>}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </DialogContent>
        <DialogActions>
          <Button color="error" variant="outlined" disabled={working || !others.length} onClick={this.revokeOthers}>{this.tr('session.mine.revokeOthers', 'Encerrar as outras')}{others.length ? ` (${others.length})` : ''}</Button>
          <Button onClick={onClose}>{this.tr('close', 'Fechar')}</Button>
        </DialogActions>
      </Dialog>
    );
  }
}
